'use client';

// eslint-disable-next-line no-restricted-imports
import dayjs from 'dayjs';
import classnames from 'classnames';
import { useContext, useEffect, useRef } from 'react';
import { StreamSessionContext } from '@providers/streamSessionProvider';
import { Container, VideoWrapper } from '@stadionhq-v3/base-ui';
import {
  StreamProvider,
  StreamVideoPlayer,
  useStreamPlaybackAccess,
} from '@stadionhq-v3/stream-ui';
import { forceSignOut } from '@utils/auth/forceSignOut';
import { LoadingTemplate } from '@components/templates/loading-template';
import { Play } from '@components/icons';
import Image from 'next/image';
import VodOverlay from './vodOverlay.component';
import { VideoDetailParams } from '../videoDetailTemplate.types';
import styles from '../videoDetailTemplate.module.scss';

const VideoTemplate = ({
  searchParams,
  dictionary,
  page,
  packagePageUrl,
}: VideoDetailParams) => {
  const classes = classnames([styles.container]);
  const context = useContext(StreamSessionContext); // Access the stream session context
  const forceSignoutRef = useRef(() => forceSignOut(context?.ssoJwt as string));
  const { cloudmatrixEntry, playbackAccess, loading } = useStreamPlaybackAccess(
    page?.cloudMatrixData?.itemData[0]?.mediaData.entryId || '',
    context?.ssoJwt as string,
    forceSignoutRef.current,
    true
  );

  // Update the ref when ssoJwt is set
  useEffect(() => {
    if (context?.ssoJwt) {
      forceSignoutRef.current = () => forceSignOut(context.ssoJwt as string);
    }
  }, [context?.ssoJwt]);

  // Ensure the component is used within a StreamSessionProvider
  if (!context) {
    throw new Error(
      'StreamSessionContext must be used within a StreamSessionProvider'
    );
  }

  const { account, ssoJwt } = context;

  const isLive = cloudmatrixEntry?.mediaData.mediaType === 'Live';

  const isStream =
    searchParams?.provider?.includes('stream') ||
    page?.video?.provider?.includes('stream');
  const videoDuration = cloudmatrixEntry?.metaData.VideoDuration || 0;
  const leftoverSeconds = videoDuration % 60;

  return (
    <div>
      <section className={classes}>
        {isStream && (
          <Container>
            {loading && (
              <div className={styles.loader}>
                <LoadingTemplate label={dictionary.loading} />
              </div>
            )}

            {cloudmatrixEntry && !loading && (
              <StreamProvider token={ssoJwt ?? ''}>
                <VideoWrapper
                  duration={{
                    text:
                      page?.video?.duration ||
                      `${Math.floor(videoDuration / 60)}:${leftoverSeconds < 10 ? '0' : ''}${leftoverSeconds}`,
                  }}
                  cardIcon={{ icon: <Play /> }}
                  image={
                    <Image
                      src={
                        page?.video?.thumbnailImageUrl ||
                        cloudmatrixEntry?.mediaData.thumbnailUrl
                      }
                      alt={page?.title || ''}
                      width={1920}
                      height={1080}
                    />
                  }
                >
                  {playbackAccess && cloudmatrixEntry?.mediaData && (
                    <StreamVideoPlayer
                      classNames={[styles.video as string]}
                      videoId={cloudmatrixEntry?.mediaData.entryId}
                      options={{ autoplay: false, muted: false }}
                      loader={<LoadingTemplate label={dictionary.loading} />}
                    />
                  )}
                </VideoWrapper>

                {!account && !playbackAccess && (
                  <VodOverlay
                    videoData={cloudmatrixEntry}
                    dictionary={{
                      title: isLive
                        ? dictionary.liveSubscribersOnlyTitle
                        : dictionary.subscribersOnlyTitle,
                      subTitle: isLive
                        ? dictionary.liveSubscribersOnlyDescription
                        : dictionary.subscribersOnlyDescription,
                      subscribeCtaLabel: dictionary.logInCTA,
                      purchaseCtaLabel: dictionary.purchaseSubscriptionCTALabel,
                    }}
                    showSignInCta
                  />
                )}

                {account && !playbackAccess && (
                  <VodOverlay
                    purchaseLink={{
                      url: packagePageUrl ?? '',
                    }}
                    videoData={cloudmatrixEntry}
                    dictionary={{
                      title: isLive
                        ? dictionary.livePurchaseSubscriptionTitle
                        : dictionary.purchaseSubscriptionTitle,
                      subTitle: isLive
                        ? dictionary.livePurchaseSubscriptionDescription
                        : dictionary.purchaseSubscriptionDescription,
                      subscribeCtaLabel: dictionary.logInCTA,
                      purchaseCtaLabel: isLive
                        ? dictionary.livePurchaseSubscriptionCTALabel
                        : dictionary.purchaseSubscriptionCTALabel,
                    }}
                  />
                )}

                <div className={styles.details}>
                  <div className={styles.info}>
                    <div className={styles.infoHead}>
                      {cloudmatrixEntry?.metaData.category && (
                        <div className={styles.category}>
                          {cloudmatrixEntry?.metaData.category}
                        </div>
                      )}
                      {cloudmatrixEntry?.publicationData.releaseFrom && (
                        <div className={styles.publishDate}>
                          {dayjs(
                            cloudmatrixEntry?.publicationData.releaseFrom
                          ).format("DD MMM 'YY")}
                        </div>
                      )}
                    </div>
                    <div className={styles.infoBody}>
                      {cloudmatrixEntry?.metaData.title && (
                        <div className={styles.title}>
                          {cloudmatrixEntry?.metaData.title}
                        </div>
                      )}
                      {cloudmatrixEntry?.metaData.body && (
                        <div className={styles.body}>
                          {cloudmatrixEntry?.metaData.body}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </StreamProvider>
            )}
          </Container>
        )}
      </section>
    </div>
  );
};

export default VideoTemplate;
